<script setup lang="ts">
import { toRefs, ref, inject, Ref, computed, watch } from 'vue';
import SelectButton from 'primevue/selectbutton';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

import { useOrganizationContractsStore } from '@/store';
import { GroupEventInterface } from '@/types';
import { CONTRACT_ENUMS } from '@/enum';

interface Props {
  loading: boolean;
  groups: GroupEventInterface[];
  scope: string;
}
enum GROUP_EVENT_FORM_TYPE {
  PERCENTAGE = 0,
  FLAT_FEE = 1,
}
const menu = [
  { id: GROUP_EVENT_FORM_TYPE.PERCENTAGE, label: 'Percentage' },
  { id: GROUP_EVENT_FORM_TYPE.FLAT_FEE, label: 'Flat Fee' },
];

const contractStore = useOrganizationContractsStore();
const props = defineProps<Props>();
const { loading, groups, scope } = toRefs(props);
const selectedMainForm = inject<Ref<CONTRACT_ENUMS.FORMS>>('selectedForm', ref(CONTRACT_ENUMS.FORMS.FLAT_FEE));
const canEdit = contractStore.canEditOrCreateContracts;
const formDisplayText = computed<string>(() => {
  if (selectedMainForm.value == CONTRACT_ENUMS.FORMS.FLAT_FEE) {
    return 'Flat Fee';
  }
  return 'Per Student';
});
const selectedForm = computed({
  get() {
    const percentage = groups.value.filter((group) => group.active).every((group) => !group.attendees_rate_is_fixed);

    return percentage ? GROUP_EVENT_FORM_TYPE.PERCENTAGE : GROUP_EVENT_FORM_TYPE.FLAT_FEE;
  },
  set(value) {
    groups.value.forEach((group) => {
      group.attendees_rate_is_fixed = value === GROUP_EVENT_FORM_TYPE.PERCENTAGE ? 0 : 1;
    });
  },
});

const errorData = computed(() => {
  const errors = v$.value.$each.$response.$errors;
  return errors?.length
    ? errors
        .map((e) => {
          return Object.values(e).flat();
        })
        .flat()
    : null;
});

const attendeeRateCheck: (value: number, data: GroupEventInterface) => boolean = (value, data) => {
  if (!data.active) return true;
  if (!data.attendees_rate) return false;

  return true;
};

const noShowRateCheck: (value: number, data: GroupEventInterface) => boolean = (value, data) => {
  if (!data.active) return true;
  if (!data.noshow_rate) return false;

  return true;
};

const visibleGroups = computed(() => {
  return groups.value.filter((group) => !group.hidden);
});

const rules = computed(() => {
  return {
    $each: helpers.forEach({
      attendees_rate: {
        greaterThanZero: helpers.withMessage('Rate must be greater than 0', attendeeRateCheck),
      },
      noshow_rate: {
        greaterThanZero: helpers.withMessage('Rate must be greater than 0', noShowRateCheck),
      },
    }),
  };
});

const v$ = useVuelidate(rules, visibleGroups, { $scope: scope.value, $autoDirty: true });

const unlimitedGroupEvents = computed({
  get() {
    const defaultEnabledContractTypes = [CONTRACT_ENUMS.FORMS.FLAT_FEE, CONTRACT_ENUMS.FORMS.PER_STUDENT];
    if (defaultEnabledContractTypes.includes(selectedMainForm.value)) {
      return true;
    }
    return groups.value[0].active;
  },
  set(value) {
    groups.value[0].active = value;

    // sets the 1, 2, and 3 group objects to inactive
    if (groups.value[0].active) {
      groups.value.forEach((group) => {
        if (group.number_of_attendees) {
          group.active = false;
        }
      });
    } else {
      groups.value[1].active = true;
    }
  },
});

// Watch function that resets the rate values for inactive, visible inputs
watch([selectedForm, unlimitedGroupEvents], () => {
  groups.value.forEach((group) => {
    if (!group.hidden) {
      group.attendees_rate = 0;
      group.noshow_rate = 0;
    }
  });
});
// Watch function to automatically set the 2 attendee toggle active if 3 is active.
// Also sets 1 attendee and unlimited active to false if either 2 or 3 attendee is chosen
// If unlimited, 2, or 3 is not active, the 1 attendee object becomes active
watch(
  [visibleGroups],
  () => {
    const twoOrThreeAttendeesSelected = visibleGroups.value.some((group) => group.active);
    const threeAttendeesSelected = visibleGroups.value.some((group) => group.active && group.number_of_attendees === 3);
    if (!twoOrThreeAttendeesSelected) {
      if (!unlimitedGroupEvents.value) groups.value[1].active = true;
      else groups.value[1].active = false;
    } else {
      if (threeAttendeesSelected) {
        groups.value[2].active = true;
      }
      groups.value[0].active = false;
      groups.value[1].active = false;
    }
  },
  { deep: true },
);
</script>

<template>
  <div
    class="group-events-container p-3 pb-0 w-full border-round-md bg-gray-50"
    :class="{ flat: selectedMainForm !== CONTRACT_ENUMS.FORMS.HOURLY }"
  >
    <p><b>Group Events</b></p>
    <div v-if="selectedMainForm === CONTRACT_ENUMS.FORMS.HOURLY">
      <div class="flex align-items-center justify-content-between mx-0 mb-2">
        <Skeleton v-if="loading" height="24px" width="47px" />
        <div v-else class="flex col-12 align-items-center justify-content-between px-0">
          <label for="is_unlimited">Unlimited Group Events</label>
          <InputSwitch id="is_unlimited" v-model="unlimitedGroupEvents" class="switch" :disabled="!canEdit" />
        </div>
      </div>
      <div class="group-events-hourly-container col-12 p-3 bg-white border-round-lg">
        <div class="form-container w-full relative border-round-lg bg-gray-100 px-1 pt-4 mb-5">
          <DataTable :value="visibleGroups" :show-gridlines="false" :row-class="() => 'border-none'" data-key="id">
            <Column
              header="# of Attendees"
              field="active"
              body-class="flex w-full align-items-center border-none"
              class="bg-gray-100 col-6"
            >
              <template #body="{ data }">
                <InputSwitch v-model="data.active" :disabled="unlimitedGroupEvents || !canEdit" class="switch mr-2" />
                {{ data.number_of_attendees }} Attendees
              </template>
            </Column>
            <Column header="Billing" field="billing_percentage" class="bg-gray-100 border-none">
              <template #body="{ data, index }">
                <InputNumber
                  v-if="selectedForm === GROUP_EVENT_FORM_TYPE.PERCENTAGE"
                  v-model="data.attendees_rate"
                  suffix="%"
                  class="group-events-input border-round-sm"
                  :min="0"
                  :max="100"
                  :class="{ 'p-invalid': v$.$each?.$response?.$errors[index]?.attendees_rate?.length }"
                  :disabled="!data.active || unlimitedGroupEvents || !canEdit"
                  @blur="v$.$touch"
                  @focus="(e: Event) => e?.target?.select()"
                />
                <InputNumber
                  v-else
                  v-model="data.attendees_rate"
                  class="group-events-input border-round-sm"
                  mode="currency"
                  locale="en-US"
                  :min-fraction-digits="2"
                  currency="USD"
                  :min="0"
                  :class="{ 'p-invalid': v$.$each.$response.$errors[index]?.attendees_rate?.length }"
                  :disabled="!data.active || unlimitedGroupEvents || !canEdit"
                  @blur="v$.$touch"
                  @focus="(e: Event) => e?.target?.select()"
                />
              </template>
            </Column>
            <Column header="No Show / Late Cancel" field="no_show_percentage" class="bg-gray-100 col-3 border-none">
              <template #body="{ data, index }">
                <InputNumber
                  v-if="selectedForm === GROUP_EVENT_FORM_TYPE.PERCENTAGE"
                  v-model="data.noshow_rate"
                  suffix="%"
                  class="group-events-input border-round-sm"
                  :min="0"
                  :max="100"
                  :class="{ 'p-invalid': v$.$each.$response.$errors[index]?.noshow_rate?.length }"
                  :disabled="!data.active || unlimitedGroupEvents || !canEdit"
                  @blur="v$.$touch"
                  @focus="(e: Event) => e?.target?.select()"
                />
                <InputNumber
                  v-else
                  v-model="data.noshow_rate"
                  class="group-events-input border-round-sm"
                  :min="0"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  :min-fraction-digits="2"
                  :class="{ 'p-invalid': v$.$each.$response.$errors[index]?.noshow_rate?.length }"
                  :disabled="!data.active || unlimitedGroupEvents || !canEdit"
                  @blur="v$.$touch"
                  @focus="(e: Event) => e?.target?.select()"
                />
              </template>
            </Column>
            <template #empty>No group event data</template>
          </DataTable>
        </div>
        <SelectButton
          v-model="selectedForm"
          class="attendee-select-button"
          :options="menu"
          option-label="label"
          option-value="id"
          :disabled="!canEdit"
        />
      </div>
      <div v-for="error of errorData" :key="error.$uid" class="input-errors mt-1">
        <div class="p-error">{{ error.$message }}</div>
      </div>
    </div>
    <div v-else class="mt-3">
      <p>
        <i>{{ formDisplayText }}</i>
        contracts allow unlimited group events
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.group-events-container {
  max-height: 282px;
  height: 282px;
  min-width: 412px;
}
.flat {
  max-height: 122px;
  height: 122px;
}
:deep(.p-inputnumber-input) {
  width: 100% !important;
  font-size: 14px;
  padding: 7px;
  height: 20px;
}
.group-events-hourly-container {
  height: 180px;
  display: grid;
  grid-template-columns: max-content;
  .attendee-select-button {
    grid-area: 1/2;
    align-self: start;
    justify-self: start;
  }
  .form-container {
    grid-area: 1/2;
    top: 20px;
    min-width: 346px;

    :deep(.p-datatable-wrapper) {
      overflow-x: hidden;

      .p-datatable-table {
        .p-datatable-thead {
          font-size: 10px;
          line-height: 12px;
          color: $grayDark;
          padding: 0;

          tr th {
            vertical-align: top;
            padding: 0 0 8px 16px;
            font-weight: 300;
          }
        }
        .p-datatable-tbody {
          font-size: 14px;
          line-height: 18px;

          tr td {
            padding: 0 0 8px 16px;

            span.disabled {
              color: $grayMedium;
            }
          }

          .group-events-input {
            width: 73px;
            height: 24px;

            .p-inputnumber-input {
              height: 24px;
            }

            .p-inputnumber-input:disabled {
              border-color: $white;
              background-color: $grayMedium;
            }
          }
        }
      }
    }
  }
}
</style>
